import type { LandingSourceKey } from './landingSourceKey';

export type KeyedLandingVariant = 'default' | 'partner' | 'magnet';

export interface KeyedLandingCopy {
  variant: KeyedLandingVariant;
  kicker: string;
  headline: string;
  headlineSecondary: string;
  sub: string;
  cta: string;
  showExplainerLink: boolean;
}

function humanizeToken(raw: string): string {
  return raw
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Resolve guest 1b–compatible copy for magnet / partner keyed landings. */
export function resolveKeyedLandingCopy(key: LandingSourceKey | null): KeyedLandingCopy {
  const partner = key?.referral_partner?.trim();
  const magnet = key?.source_magnet?.trim() || key?.conversation_starter?.trim();
  const product = key?.target_product?.trim();
  const starter = key?.conversation_starter?.trim();

  const education =
    Boolean(magnet && /explainer|education|method|attention|vgr/i.test(magnet)) ||
    Boolean(starter && /explainer|education|method|attention|vgr/i.test(starter));

  if (partner) {
    return {
      variant: 'partner',
      kicker: `referred by ${humanizeToken(partner)}`,
      headline: 'Your partner opened the bridge.',
      headlineSecondary: 'Start the conversation that turns intent into coordinated execution.',
      sub: product
        ? `This path is oriented toward ${humanizeToken(product)}. Name where you want the business to go — we will meet you there.`
        : 'Name where you want the business to go. We will open a prospector session that keeps this referral context.',
      cta: 'Continue with your prospector',
      showExplainerLink: education,
    };
  }

  if (magnet) {
    return {
      variant: 'magnet',
      kicker: product ? `${humanizeToken(product)} · ${humanizeToken(magnet)}` : humanizeToken(magnet),
      headline: starter ? humanizeToken(starter) : 'Start from this opening.',
      headlineSecondary: 'One conversation. Your source key stays with the prospector.',
      sub: 'No demo theater. No pricing maze. Just the shortest path from this magnet into a living model of where you are headed.',
      cta: 'Start the conversation',
      showExplainerLink: education || /explainer/i.test(magnet),
    };
  }

  return {
    variant: 'default',
    kicker: product ? humanizeToken(product) : 'keyed landing',
    headline: 'Where do you want to take your business?',
    headlineSecondary: 'Start there — even if it is still taking shape.',
    sub: 'This link carried entry context into Krysalis. Continue into a prospector session and we will keep that context.',
    cta: "Let's figure out what you need",
    showExplainerLink: false,
  };
}
